import { useState, useEffect, useRef } from "react";
import { axios, AxiosError } from "@root/libs/axios";

export const SALARY_TYPE_PERCENTAGE = "5";

export type UseSalaryDataParams = {
  selectedSalaryType: string;
  salaryMoneyOptionsUrl: string;
  onError: (message: string) => void;
};

export type SalaryData = {
  salaryMinOptions: { label: string; value: string }[];
  salaryMaxOptions: { label: string; value: string }[];
  isLoading: boolean;
};

type SalaryMoneyOptionsResponse = {
  min: Record<string, number>;
  max: Record<string, number>;
};

export const useSalaryData = (params: UseSalaryDataParams): SalaryData => {
  const { selectedSalaryType, salaryMoneyOptionsUrl, onError } = params;

  const [salaryData, setSalaryData] = useState<SalaryMoneyOptionsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!selectedSalaryType || selectedSalaryType === SALARY_TYPE_PERCENTAGE) {
      setSalaryData(null);
      setIsLoading(false);
      return () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }

    const fetchSalaryData = async () => {
      try {
        setIsLoading(true);

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const response = await axios.post<SalaryMoneyOptionsResponse>(
          salaryMoneyOptionsUrl,
          { salaryTypeId: Number(selectedSalaryType) },
          { signal: controller.signal },
        );

        setSalaryData(response.data);
      } catch (error) {
        if (error instanceof AxiosError && error.name !== "CanceledError") {
          onError("給与オプションデータを取得できませんでした");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchSalaryData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedSalaryType, salaryMoneyOptionsUrl, onError]);

  const createOptions = (data: Record<string, number> | undefined) => {
    if (!data) return [];
    return Object.entries(data).map(([label, value]) => ({
      label,
      value: String(value),
    }));
  };

  const salaryMinOptions = createOptions(salaryData?.min);
  const salaryMaxOptions = createOptions(salaryData?.max);

  return {
    salaryMinOptions,
    salaryMaxOptions,
    isLoading,
  };
};
